import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiCheck, FiX, FiAlertCircle, FiLink, FiTrendingUp, FiPackage } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';
import './WooCommerce.css';

const WooCommerce = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingOaqzKey, setSavingOaqzKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [formData, setFormData] = useState({
    store_url: '',
    consumer_key: '',
    consumer_secret: '',
    oaqz_api_key: ''
  });
  const [testResult, setTestResult] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncingStock, setSyncingStock] = useState(false);
  const [shopifyConfig, setShopifyConfig] = useState({ configured: false });
  const [shopifyForm, setShopifyForm] = useState({ shop_domain: '', client_id: '', client_secret: '', location_id: '' });
  const [savingShopify, setSavingShopify] = useState(false);
  const [syncingShopifyStock, setSyncingShopifyStock] = useState(false);
  const [shopifySyncResult, setShopifySyncResult] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchMappings();
    fetchSyncStatus();
    fetchShopifyConfig();
    
    // Poll sync status elke 30 seconden
    const statusInterval = setInterval(fetchSyncStatus, 30000);
    return () => clearInterval(statusInterval);
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/woocommerce/config');
      if (response.data && response.data.store_url) {
        setConfig(response.data);
        setFormData({
          store_url: response.data.store_url,
          consumer_key: '••••••••••••', // Masked
          consumer_secret: '••••••••••••', // Masked
          oaqz_api_key: response.data.has_oaqz_key ? '••••••••' : ''
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await api.get('/woocommerce/sync/mappings');
      setMappings(response.data);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/woocommerce/sync/status');
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchShopifyConfig = async () => {
    try {
      const response = await api.get('/woocommerce/shopify-config');
      const data = response.data || { configured: false };
      setShopifyConfig(data);
      setShopifyForm((prev) => ({
        ...prev,
        shop_domain: data.shop_domain || '',
        client_id: data.client_id || '',
        location_id: data.location_id || '',
        client_secret: data.has_client_secret ? '••••••••••••' : ''
      }));
    } catch (error) {
      console.error('Error fetching Shopify config:', error);
    }
  };

  const handleSyncStock = async () => {
    if (!window.confirm('Weet je zeker dat je de voorraad wilt synchroniseren vanuit WooCommerce? Lokale voorraad wordt bijgewerkt met WooCommerce voorraad.')) {
      return;
    }

    setSyncingStock(true);
    try {
      const response = await api.post('/woocommerce/sync/stock');
      if (response.data.success) {
        alert(`Voorraad gesynchroniseerd!\n${response.data.updated} producten bijgewerkt\n${response.data.errors} fouten`);
        fetchSyncStatus();
      } else {
        alert('Synchronisatie mislukt: ' + (response.data.message || 'Onbekende fout'));
      }
    } catch (error) {
      console.error('Error syncing stock:', error);
      alert('Fout bij synchroniseren: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncingStock(false);
    }
  };

  const handleSaveShopifyConfig = async () => {
    setSavingShopify(true);
    try {
      await api.post('/woocommerce/shopify-config', shopifyForm);
      alert('Shopify configuratie opgeslagen.');
      fetchShopifyConfig();
    } catch (error) {
      console.error('Error saving Shopify config:', error);
      alert('Fout bij opslaan Shopify config: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingShopify(false);
    }
  };

  const handleSyncShopifyStock = async () => {
    if (!window.confirm('Voorraad vanuit inkoopsoftware naar Shopify pushen?')) return;
    setSyncingShopifyStock(true);
    setShopifySyncResult(null);
    try {
      const response = await api.post('/woocommerce/sync/shopify-stock');
      setShopifySyncResult(response.data);
      alert(`Shopify sync voltooid: ${response.data.updated} bijgewerkt, ${response.data.missing} zonder SKU-match, ${response.data.errors} fouten`);
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Shopify sync mislukt: ' + msg);
    } finally {
      setSyncingShopifyStock(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/woocommerce/config', formData);
      setTestResult(null);
      alert('Configuratie opgeslagen!');
      fetchConfig();
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveOaqzKey = async () => {
    const key = formData.oaqz_api_key?.trim();
    if (!key || key === '••••••••') {
      alert('Vul eerst de Order Afhandeling API key in.');
      return;
    }
    setSavingOaqzKey(true);
    try {
      await api.patch('/woocommerce/config/oaqz-key', { oaqz_api_key: key });
      alert('Order Afhandeling API key opgeslagen!');
      fetchConfig();
    } catch (err) {
      alert('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingOaqzKey(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Send form data to test connection
      const response = await api.post('/woocommerce/test', formData);
      setTestResult(response.data);
      // Niet fetchConfig() na success: dat overschrijft het formulier met placeholders.
      // Daardoor wordt bij "Configuratie Bijwerken" de oude config bewaard ipv de net geteste.
    } catch (error) {
      let errorMessage = 'Test mislukt';
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        errorMessage = 'Network error: Kan geen verbinding maken met de backend server. Controleer of de server draait op poort 5000.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setTestResult({ 
        success: false, 
        message: errorMessage,
        errorCode: error.code
      });
    } finally {
      setTesting(false);
    }
  };

  const handleReconnect = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Force reconnect with saved config
      const response = await api.post('/woocommerce/reconnect');
      setTestResult(response.data);
      if (response.data.success) {
        fetchConfig();
        alert('Verbinding hersteld!');
      } else {
        alert('Verbinding mislukt: ' + response.data.message);
      }
    } catch (error) {
      let errorMessage = 'Reconnect mislukt';
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        errorMessage = 'Network error: Kan geen verbinding maken met de backend server.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setTestResult({ 
        success: false, 
        message: errorMessage,
        errorCode: error.code
      });
      alert('Reconnect mislukt: ' + errorMessage);
    } finally {
      setTesting(false);
    }
  };

  const handleImport = async () => {
    if (!window.confirm('Weet je zeker dat je alle producten uit WooCommerce wilt importeren? Bestaande producten worden bijgewerkt.')) {
      return;
    }

    setImporting(true);
    setImportResults(null);
    try {
      const response = await api.post('/woocommerce/import');
      setImportResults(response.data);
      alert(`Import voltooid: ${response.data.successful} succesvol, ${response.data.failed} mislukt`);
      // Refresh page to show new products
      window.location.reload();
    } catch (error) {
      console.error('Error importing:', error);
      alert('Fout bij importeren: ' + (error.response?.data?.message || error.message));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="woocommerce">
      <h1 className="page-title">WooCommerce Integratie</h1>

      <div className="wc-sections">
        {/* Configuratie Sectie */}
        <div className="wc-section">
          <h2>WooCommerce Configuratie</h2>
          <p className="section-description">
            Verbind je inkoopsoftware met je WooCommerce webshop. Je hebt een Consumer Key en Consumer Secret nodig.
            Deze kun je aanmaken in WooCommerce onder: Instellingen → Geavanceerd → REST API.
          </p>

          {config?.credentials_need_refresh && (
            <div className="wc-credentials-warning">
              <strong>Credentials moeten opnieuw worden ingevoerd.</strong> De opgeslagen sleutels zijn ongeldig.
              Kopieer de Consumer Key en Secret uit WooCommerce (REST API) en plak ze hier, dan op Configuratie Bijwerken.
            </div>
          )}

          <form onSubmit={handleSubmit} className="wc-form" autoComplete="off">
            <div className="form-group">
              <label>WooCommerce Store URL *</label>
              <input
                type="url"
                value={formData.store_url}
                onChange={(e) => setFormData({ ...formData, store_url: e.target.value })}
                placeholder="https://jouwwebshop.nl"
                required
                disabled={!!config}
                autoComplete="off"
                name="woocommerce-store-url"
              />
              {config && (
                <small className="form-hint">Bewerk configuratie door nieuwe waarden in te voeren</small>
              )}
            </div>

            <div className="form-group">
              <label>Consumer Key *</label>
              <input
                type="text"
                value={formData.consumer_key}
                onChange={(e) => setFormData({ ...formData, consumer_key: e.target.value })}
                placeholder="ck_..."
                required
                autoComplete="off"
                data-lpignore="true"
                name="woocommerce-consumer-key"
              />
            </div>

            <div className="form-group">
              <label>Consumer Secret *</label>
              <input
                type="password"
                value={formData.consumer_secret}
                onChange={(e) => setFormData({ ...formData, consumer_secret: e.target.value })}
                placeholder="cs_..."
                required
                autoComplete="off"
                data-lpignore="true"
                name="woocommerce-consumer-secret"
              />
            </div>

            <div className="form-group">
              <label>Order Afhandeling API key (voor Magazijn → label + print)</label>
              <div className="oaqz-key-row">
                <input
                  type="password"
                  value={formData.oaqz_api_key}
                  onChange={(e) => setFormData({ ...formData, oaqz_api_key: e.target.value })}
                  placeholder="Token uit WordPress: Order Afhandeling → Instellingen"
                  autoComplete="off"
                  data-lpignore="true"
                  name="woocommerce-oaqz-key"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-oaqz-save"
                  onClick={handleSaveOaqzKey}
                  disabled={savingOaqzKey}
                  title="Sla alleen deze API key op"
                >
                  {savingOaqzKey ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
              <small className="form-hint">Vul de API key in en klik op Opslaan. Token uit Order Afhandeling plugin (WooCommerce → Orderverwerking → Instellingen).</small>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleTest} disabled={testing}>
                {testing ? 'Testen...' : 'Test Verbinding'}
              </button>
              <button type="submit" className="btn btn-primary">
                {config ? 'Configuratie Bijwerken' : 'Configuratie Opslaan'}
              </button>
            </div>
            <p className="form-hint" style={{ marginTop: 8 }}>
              Test de verbinding, en klik daarna op <strong>Configuratie Opslaan/Bijwerken</strong> om de gegevens te bewaren. Anders werken Magazijn en andere pagina's niet na het verlaten.
            </p>
          </form>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? <FiCheck /> : <FiX />}
              <span>{testResult.message}</span>
            </div>
          )}

          {config && (
            <div className="config-info">
              <h3>Huidige Configuratie</h3>
              <p><strong>Store URL:</strong> {config.store_url}</p>
              <p><strong>Status:</strong> {config.is_active ? 'Actief' : 'Inactief'}</p>
              {config.last_sync && (
                <p><strong>Laatste Sync:</strong> {new Date(config.last_sync).toLocaleString('nl-NL')}</p>
              )}
              <div style={{ marginTop: '1rem' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleReconnect} 
                  disabled={testing}
                  title="Test en herstel de verbinding met de opgeslagen configuratie"
                >
                  <FiRefreshCw className={testing ? 'spinning' : ''} />
                  {testing ? 'Verbinden...' : 'Herstel Verbinding'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Voorraad Sync Sectie */}
        {config && (
          <div className="wc-section">
            <h2>Automatische Voorraad Controle</h2>
            <p className="section-description">
              De voorraad wordt automatisch elke 4 uur <strong>vanuit WooCommerce</strong> gehaald.
              Alleen de lokale voorraad in de inkoopsoftware wordt bijgewerkt — er wordt nooit voorraad terug naar WooCommerce gestuurd.
            </p>

            {syncStatus && (
              <div className="sync-status-info" style={{ 
                background: '#f0fffe', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                border: '1px solid #3FDFCF'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {syncStatus.isActive ? '✓ Actief' : '✗ Inactief'}
                    {syncStatus.isRunning && <span style={{ color: '#3FDFCF', marginLeft: '1rem' }}>⏳ Synchroniseren...</span>}
                  </div>
                  {syncStatus.lastSync && (
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      Laatste sync: {new Date(syncStatus.lastSync).toLocaleString('nl-NL')}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="sync-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleSyncStock}
                disabled={syncingStock || (syncStatus && syncStatus.isRunning)}
                title="Handmatig voorraad synchroniseren vanuit WooCommerce"
              >
                <FiPackage className={syncingStock ? 'spinning' : ''} />
                {syncingStock ? 'Synchroniseren...' : 'Sync Voorraad Nu'}
              </button>
            </div>
          </div>
        )}

        {/* Shopify Voorraad Push Sectie */}
        <div className="wc-section">
          <h2>Shopify Voorraad Sync</h2>
          <p className="section-description">
            Push voorraad uit de inkoopsoftware naar Shopify. Access token wordt automatisch per sync vernieuwd met client ID/secret.
          </p>
          <div className="wc-form">
            <div className="form-group">
              <label>Shopify domein</label>
              <input
                type="text"
                value={shopifyForm.shop_domain}
                onChange={(e) => setShopifyForm((f) => ({ ...f, shop_domain: e.target.value }))}
                placeholder="uipmcj-8w.myshopify.com"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Client ID</label>
              <input
                type="text"
                value={shopifyForm.client_id}
                onChange={(e) => setShopifyForm((f) => ({ ...f, client_id: e.target.value }))}
                placeholder="Shopify app client id"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Client Secret</label>
              <input
                type="password"
                value={shopifyForm.client_secret}
                onChange={(e) => setShopifyForm((f) => ({ ...f, client_secret: e.target.value }))}
                placeholder={shopifyConfig?.has_client_secret ? '(opgeslagen)' : 'Shopify app client secret'}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Location ID</label>
              <input
                type="text"
                value={shopifyForm.location_id}
                onChange={(e) => setShopifyForm((f) => ({ ...f, location_id: e.target.value }))}
                placeholder="109681803604"
                autoComplete="off"
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleSaveShopifyConfig} disabled={savingShopify}>
                {savingShopify ? 'Opslaan...' : 'Shopify Config Opslaan'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSyncShopifyStock}
                disabled={syncingShopifyStock || !shopifyConfig?.configured}
                title={shopifyConfig?.configured ? 'Push voorraad naar Shopify' : 'Sla eerst Shopify config op'}
              >
                <FiPackage className={syncingShopifyStock ? 'spinning' : ''} />
                {syncingShopifyStock ? 'Syncen...' : 'Sync Voorraad naar Shopify'}
              </button>
            </div>
            <p className="form-hint">
              Matching gebeurt op SKU. Alleen producten met dezelfde SKU in Shopify en inkoopsoftware worden bijgewerkt.
            </p>
          </div>

          {shopifySyncResult && (
            <div className="sync-results">
              <h3>Shopify Sync Resultaat</h3>
              <div className="sync-stats">
                <div className="stat">
                  <span className="stat-label">Bijgewerkt</span>
                  <span className="stat-value">{shopifySyncResult.updated || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Geen match (SKU)</span>
                  <span className="stat-value">{shopifySyncResult.missing || 0}</span>
                </div>
                <div className="stat stat-error">
                  <span className="stat-label">Fouten</span>
                  <span className="stat-value">{shopifySyncResult.errors || 0}</span>
                </div>
              </div>
              {Array.isArray(shopifySyncResult.details) && shopifySyncResult.details.length > 0 && (
                <div className="sync-details">
                  <h4>Foutdetails</h4>
                  <ul>
                    {shopifySyncResult.details.slice(0, 10).map((d, idx) => (
                      <li key={idx} className="error">
                        <FiX />
                        <span>{d.sku || d.product_id}: {d.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Import Sectie */}
        {config && (
          <div className="wc-section">
            <h2>Import van WooCommerce</h2>
            <p className="section-description">
              Importeer producten en verkoopdata uit je WooCommerce webshop naar de inkoopsoftware.
            </p>

            <div className="sync-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleImport}
                disabled={importing}
              >
                <FiRefreshCw className={importing ? 'spinning' : ''} />
                {importing ? 'Importeren...' : 'Importeer Producten'}
              </button>
              <a 
                href="/verkoopdata" 
                className="btn btn-secondary"
                style={{ textDecoration: 'none', display: 'inline-flex' }}
              >
                <FiTrendingUp />
                Importeer Verkoopdata
              </a>
            </div>

            {importResults && (
              <div className="sync-results">
                <h3>Import Resultaten</h3>
                <div className="sync-stats">
                  <div className="stat">
                    <span className="stat-label">Totaal:</span>
                    <span className="stat-value">{importResults.total}</span>
                  </div>
                  <div className="stat stat-success">
                    <span className="stat-label">Succesvol:</span>
                    <span className="stat-value">{importResults.successful}</span>
                  </div>
                  <div className="stat stat-error">
                    <span className="stat-label">Mislukt:</span>
                    <span className="stat-value">{importResults.failed}</span>
                  </div>
                </div>

                {importResults.results && importResults.results.length > 0 && (
                  <div className="sync-details">
                    <h4>Details:</h4>
                    <ul>
                      {importResults.results.slice(0, 10).map((result, index) => (
                        <li key={index} className={result.success ? 'success' : 'error'}>
                          {result.success ? <FiCheck /> : <FiX />}
                          <span>{result.product}: {result.action || (result.success ? 'Geïmporteerd' : result.error)}</span>
                        </li>
                      ))}
                    </ul>
                    {importResults.results.length > 10 && (
                      <p className="more-results">... en {importResults.results.length - 10} meer</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Sync Mappings Sectie */}
        {config && mappings.length > 0 && (
          <div className="wc-section">
            <h2>Gesynchroniseerde Producten</h2>
            <div className="mappings-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>WooCommerce ID</th>
                    <th>Laatste Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((mapping) => (
                    <tr key={mapping.id}>
                      <td>{mapping.product_naam}</td>
                      <td>{mapping.sku || '-'}</td>
                      <td>
                        <a 
                          href={`${config.store_url}/wp-admin/post.php?post=${mapping.woocommerce_id}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wc-link"
                        >
                          {mapping.woocommerce_id} <FiLink />
                        </a>
                      </td>
                      <td>{new Date(mapping.last_synced).toLocaleString('nl-NL')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!config && (
          <div className="wc-section">
            <div className="info-box">
              <FiAlertCircle />
              <div>
                <h3>Nog geen WooCommerce verbinding</h3>
                <p>Configureer eerst je WooCommerce verbinding hierboven om te beginnen met synchroniseren.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WooCommerce;
